import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { BrandProvider } from "./context/BrandContext";
import { DemoProvider } from "./context/DemoContext";
import { Toaster } from "./components/ui/toaster";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";
import MainLayout from "./components/MainLayout";
import Login from "./pages/Login";
import LoginDebug from "./pages/LoginDebug";
import FirstTimeSetup from "./pages/FirstTimeSetup";
import SubmittedDeals from "./pages/SubmittedDeals";
import FundedDeals from "./pages/FundedDeals";
import DealDetail from "./pages/DealDetail";
import Clients from "./pages/Clients";
import ClientDetail from "./pages/ClientDetail";
import Companies from "./pages/Companies";
import Quotes from "./pages/Quotes";
import ISOProfile from "./pages/ISOProfile";
import PortfolioSummary from "./pages/PortfolioSummary";
import ArchivedDeals from "./pages/ArchivedDeals";
import KilledDeals from "./pages/KilledDeals";
import AdminPanel from "./pages/AdminPanel";
import StaffManagement from "./pages/Team";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

function App() {
  const [needsSetup, setNeedsSetup] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    checkSetupStatus();
  }, []);

  const checkSetupStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE}/auth/check-setup`);
      setNeedsSetup(response.data.needs_setup);
    } catch (error) {
      console.error('Error checking setup status:', error);
      // If the API fails, assume we need setup (safer default)
      setNeedsSetup(true);
    } finally {
      setChecking(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (needsSetup) {
    return (
      <BrandProvider>
        <BrowserRouter>
          <Routes>
            <Route path="*" element={<FirstTimeSetup />} />
          </Routes>
          <Toaster />
        </BrowserRouter>
      </BrandProvider>
    );
  }

  return (
    <BrandProvider>
      <AuthProvider>
        <DemoProvider>
        <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/login-debug" element={<LoginDebug />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <Navigate to="/funded-deals" replace />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/submitted-deals"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <MainLayout>
                    <SubmittedDeals />
                  </MainLayout>
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/funded-deals"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <FundedDeals />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/deals/:id"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <DealDetail />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/clients"
            element={
              <AdminRoute>
                <MainLayout>
                  <Clients />
                </MainLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/clients/:id"
            element={
              <AdminRoute>
                <MainLayout>
                  <ClientDetail />
                </MainLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/companies"
            element={
              <AdminRoute>
                <MainLayout>
                  <Companies />
                </MainLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/quotes"
            element={
              <AdminRoute>
                <MainLayout>
                  <Quotes />
                </MainLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/iso-profile"
            element={
              <AdminRoute>
                <MainLayout>
                  <ISOProfile />
                </MainLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/portfolio"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <PortfolioSummary />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/archived-deals"
            element={
              <AdminRoute>
                <MainLayout>
                  <ArchivedDeals />
                </MainLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/killed-deals"
            element={
              <AdminRoute adminOnly>
                <MainLayout>
                  <KilledDeals />
                </MainLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <AdminRoute adminOnly>
                <MainLayout>
                  <AdminPanel />
                </MainLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/staff"
            element={
              <AdminRoute adminOnly>
                <MainLayout>
                  <StaffManagement />
                </MainLayout>
              </AdminRoute>
            }
          />
        </Routes>
        <Toaster />
      </BrowserRouter>
      </DemoProvider>
    </AuthProvider>
    </BrandProvider>
  );
}

export default App;
